import { useState, useEffect, useRef, useLayoutEffect } from "react";
import { getUrl } from 'aws-amplify/storage';
import classes from "../../StudioErstellen/FotosInput.module.css";
import ErrorMessage from "../../StudioErstellen/ErrorMessage";

const ALLOWED_IMAGE_TYPES = new Set([
    "image/jpeg",
    "image/jpg",
    "image/pjpeg",
    "image/png",
    "image/x-png",
]);

const ALLOWED_IMAGE_EXT = /\.(jpe?g|png)$/i;

const isAllowedImageFile = (file) => {
    if (!file) return false;
    if (file.type && ALLOWED_IMAGE_TYPES.has(file.type)) return true;
    if (file.name && ALLOWED_IMAGE_EXT.test(file.name)) return true;
    return false;
};

const PreviewImageWithSkeleton = ({ photo, index }) => {
    const imgRef = useRef(null);
    const [loaded, setLoaded] = useState(() => !photo?.previewUrl);

    useEffect(() => {
        setLoaded(!photo?.previewUrl);
    }, [photo?.previewUrl]);

    /** Cached / blob / data URLs often complete before React's onLoad runs. */
    useLayoutEffect(() => {
        if (!photo?.previewUrl) return;
        const img = imgRef.current;
        if (img?.complete && img.naturalWidth > 0) {
            setLoaded(true);
        }
    }, [photo?.previewUrl]);

    return (
        <>
            {!loaded && <div className={classes.previewImageSkeleton} aria-hidden />}
            <img
                ref={imgRef}
                key={photo.previewUrl}
                src={photo.previewUrl}
                alt={`Studio preview ${photo.fileName || `photo-${index + 1}`}`}
                className={`${classes.previewImage} ${loaded ? classes.previewImageVisible : classes.previewImageHidden}`}
                draggable={false}
                decoding="async"
                onLoad={() => setLoaded(true)}
                onError={(e) => {
                    setLoaded(true);
                    console.error('Error loading image:', photo);
                    e.target.style.display = 'none';
                }}
            />
        </>
    );
};

const evaluateDragAllowDrop = (dataTransfer) => {
    const items = Array.from(dataTransfer?.items || []).filter((i) => i.kind === "file");
    if (items.length === 0) {
        if (dataTransfer?.types?.includes("Files")) {
            return true;
        }
        return false;
    }
    const files = items.map((i) => i.getAsFile()).filter(Boolean);
    if (files.length === 0) return true;
    return files.every(isAllowedImageFile);
};

export const FotosEditor = ({ validate, selectedFiles, setSelectedFiles, studioImages }) => {
    const [errors, setErrors] = useState({
        title: "",
    });
    const [isDragging, setIsDragging] = useState(false);
    const [originalImages, setOriginalImages] = useState([]);
    const [draggedIndex, setDraggedIndex] = useState(null);
    const [dragOverIndex, setDragOverIndex] = useState(null);
    const [touchStartIndex, setTouchStartIndex] = useState(null);
    const [touchStartY, setTouchStartY] = useState(null);
    const [touchCurrentY, setTouchCurrentY] = useState(null);

    // Load existing studio images with full URLs
    useEffect(() => {
        const loadPhotos = async () => {
            if (studioImages && studioImages.length > 0) {
                const photosWithUrls = await Promise.all(
                    studioImages.map(async (imageKey, index) => {
                        try {
                            // Get URL from S3 bucket
                            const result = await getUrl({ key: imageKey });
                            return {
                                id: `existing-${imageKey}-${index}`,
                                previewUrl: result.url.toString(),
                                fileName: imageKey.split('/').pop() || `image-${index + 1}`,
                                s3Key: imageKey,
                                type: 'image/jpeg',
                                isExisting: true,
                                index: index // Keep track of original order
                            };
                        } catch (error) {
                            console.error(`Error loading image ${imageKey}:`, error);
                            return null;
                        }
                    })
                );
                
                // Filter out null values and sort by original index to maintain order
                const validPhotos = photosWithUrls
                    .filter(photo => photo !== null)
                    .sort((a, b) => a.index - b.index);
                
                setSelectedFiles(validPhotos);
                setOriginalImages(validPhotos);
            } else {
                // If no existing images, reset to empty state
                setSelectedFiles([]);
                setOriginalImages([]);
            }
        };
        
        loadPhotos();
    }, [studioImages, setSelectedFiles]);
      
    const handleValidation = () => {
        if (selectedFiles.length < 5) {
            setErrors({ title: "Mindestens 5 Fotos hinzufügen. Je mehr desto besser." });
            return false;
        }

        // Return all selected files for upload (like React Native developer)
        return {
            isValid: true,
            selectedFiles: selectedFiles
        };
    };

    const revokePreviewUrl = (photo) => {
        if (photo?.previewUrl?.startsWith("blob:")) {
            URL.revokeObjectURL(photo.previewUrl);
        }
    };

    const processFiles = async (files) => {
        try {
            if (!files || files.length === 0) {
                return;
            }

            const list = Array.from(files);
            const allowed = list.filter(isAllowedImageFile);
            const rejectedCount = list.length - allowed.length;

            if (allowed.length === 0) {
                setErrors({ title: "Nur JPG- und PNG-Dateien sind erlaubt." });
                return;
            }

            const newPhotos = allowed.map((file) => {
                const uniqueId = crypto.randomUUID();

                return {
                    id: uniqueId,
                    previewUrl: URL.createObjectURL(file),
                    file,
                    fileName: `${uniqueId}-${file.name}`,
                    type: file.type,
                    isNew: true
                };
            });
            
            const updatedPhotos = [...selectedFiles, ...newPhotos];
            setSelectedFiles(updatedPhotos);

            if (rejectedCount > 0 && updatedPhotos.length < 5) {
                setErrors({
                    title: "Einige Dateien wurden ignoriert. Mindestens 5 Fotos hinzufügen (nur JPG- und PNG-Dateien).",
                });
            } else if (rejectedCount > 0) {
                setErrors({
                    title: "Einige Dateien wurden ignoriert. Nur JPG- und PNG-Dateien sind erlaubt.",
                });
            } else if (updatedPhotos.length >= 5) {
                setErrors({ title: "" });
            } else {
                setErrors({ title: "Mindestens 5 Fotos hinzufügen. Je mehr desto besser." });
            }
        } catch (error) {
            console.error('Error processing files:', error);
        }
    };

    const handleFileChange = async (event) => {
        const files = Array.from(event.target.files);
        await processFiles(files);
    };

    const handleRemovePhoto = (photoToRemove) => {
        revokePreviewUrl(photoToRemove);
        const updatedFiles = selectedFiles.filter(photo => photo.id !== photoToRemove.id);
        setSelectedFiles(updatedFiles);
    };

    // Drag and drop handlers for reordering images
    const handleDragStart = (e, index) => {
        setDraggedIndex(index);
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('text/html', e.target);
    };

    const handleImageDragOver = (e, index) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        setDragOverIndex(index);
    };

    const handleImageDragLeave = () => {
        setDragOverIndex(null);
    };

    const handleImageDrop = (e, dropIndex) => {
        e.preventDefault();
        e.stopPropagation();
        
        if (draggedIndex === null || draggedIndex === dropIndex) {
            setDraggedIndex(null);
            setDragOverIndex(null);
            return;
        }

        const updatedFiles = [...selectedFiles];
        const draggedItem = updatedFiles[draggedIndex];
        
        // Remove dragged item
        updatedFiles.splice(draggedIndex, 1);
        
        // Insert at new position
        updatedFiles.splice(dropIndex, 0, draggedItem);
        
        setSelectedFiles(updatedFiles);

        setDraggedIndex(null);
        setDragOverIndex(null);
    };

    const handleDragEnd = () => {
        setDraggedIndex(null);
        setDragOverIndex(null);
    };

    // Touch handlers for mobile reordering
    const handleTouchStart = (e, index) => {
        const touch = e.touches[0];
        setTouchStartIndex(index);
        setTouchStartY(touch.clientY);
        setTouchCurrentY(touch.clientY);
        setDraggedIndex(index);
    };

    const handleTouchMove = (e) => {
        if (touchStartIndex === null) return;
        
        e.preventDefault(); // Prevent scrolling while dragging
        const touch = e.touches[0];
        setTouchCurrentY(touch.clientY);

        // Find which element we're over using data attribute
        const elements = document.elementsFromPoint(touch.clientX, touch.clientY);
        const imageWrapper = elements.find(el => 
            el.hasAttribute('data-image-index')
        );
        
        if (imageWrapper) {
            const newIndex = parseInt(imageWrapper.getAttribute('data-image-index'));
            if (!isNaN(newIndex) && newIndex !== touchStartIndex) {
                setDragOverIndex(newIndex);
            } else if (newIndex === touchStartIndex) {
                setDragOverIndex(null);
            }
        } else {
            setDragOverIndex(null);
        }
    };

    const handleTouchEnd = (e) => {
        if (touchStartIndex === null) {
            setDraggedIndex(null);
            setDragOverIndex(null);
            return;
        }

        const touch = e.changedTouches[0];
        const elements = document.elementsFromPoint(touch.clientX, touch.clientY);
        const imageWrapper = elements.find(el => 
            el.hasAttribute('data-image-index')
        );

        if (imageWrapper) {
            const dropIndex = parseInt(imageWrapper.getAttribute('data-image-index'));

            if (!isNaN(dropIndex) && dropIndex !== touchStartIndex) {
                const updatedFiles = [...selectedFiles];
                const draggedItem = updatedFiles[touchStartIndex];
                
                updatedFiles.splice(touchStartIndex, 1);
                updatedFiles.splice(dropIndex, 0, draggedItem);
                
                setSelectedFiles(updatedFiles);
            }
        }

        setTouchStartIndex(null);
        setTouchStartY(null);
        setTouchCurrentY(null);
        setDraggedIndex(null);
        setDragOverIndex(null);
    };

    // Global touch handlers for better mobile support
    useEffect(() => {
        if (touchStartIndex === null) return;

        const handleGlobalTouchMove = (e) => {
            if (touchStartIndex === null) return;
            e.preventDefault();
            const touch = e.touches[0];
            setTouchCurrentY(touch.clientY);

            const elements = document.elementsFromPoint(touch.clientX, touch.clientY);
            const imageWrapper = elements.find(el => 
                el.hasAttribute('data-image-index')
            );
            
            if (imageWrapper) {
                const newIndex = parseInt(imageWrapper.getAttribute('data-image-index'));
                if (!isNaN(newIndex) && newIndex !== touchStartIndex) {
                    setDragOverIndex(newIndex);
                } else if (newIndex === touchStartIndex) {
                    setDragOverIndex(null);
                }
            } else {
                setDragOverIndex(null);
            }
        };

        const handleGlobalTouchEnd = (e) => {
            if (touchStartIndex === null) return;

            const touch = e.changedTouches[0];
            const elements = document.elementsFromPoint(touch.clientX, touch.clientY);
            const imageWrapper = elements.find(el => 
                el.hasAttribute('data-image-index')
            );

            if (imageWrapper) {
                const dropIndex = parseInt(imageWrapper.getAttribute('data-image-index'));

                if (!isNaN(dropIndex) && dropIndex !== touchStartIndex) {
                    const updatedFiles = [...selectedFiles];
                    const draggedItem = updatedFiles[touchStartIndex];
                    
                    updatedFiles.splice(touchStartIndex, 1);
                    updatedFiles.splice(dropIndex, 0, draggedItem);
                    
                    setSelectedFiles(updatedFiles);
                }
            }

            setTouchStartIndex(null);
            setTouchStartY(null);
            setTouchCurrentY(null);
            setDraggedIndex(null);
            setDragOverIndex(null);
        };

        document.addEventListener('touchmove', handleGlobalTouchMove, { passive: false });
        document.addEventListener('touchend', handleGlobalTouchEnd);

        return () => {
            document.removeEventListener('touchmove', handleGlobalTouchMove);
            document.removeEventListener('touchend', handleGlobalTouchEnd);
        };
    }, [touchStartIndex, selectedFiles, setSelectedFiles]);

    // Drag and drop handlers for file upload
    const handleContainerDragOver = (e) => {
        // Don't handle container drag if we're reordering items
        if (draggedIndex !== null) {
            return;
        }
        
        e.preventDefault();
        e.stopPropagation();
        const allowDrop = evaluateDragAllowDrop(e.dataTransfer);
        e.dataTransfer.dropEffect = allowDrop ? "copy" : "none";
        setIsDragging(allowDrop);
    };

    const handleContainerDragLeave = (e) => {
        // Don't handle container drag leave if we're reordering items
        if (draggedIndex !== null) {
            return;
        }
        
        e.preventDefault();
        e.stopPropagation();
        // Only set isDragging to false if we're actually leaving the container
        if (!e.currentTarget.contains(e.relatedTarget)) {
            setIsDragging(false);
        }
    };

    const handleContainerDrop = async (e) => {
        // Don't handle drop if we're reordering items (check if draggedIndex is set)
        if (draggedIndex !== null) {
            return;
        }
        
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
        
        const files = Array.from(e.dataTransfer.files);
        if (files.length === 0) {
            return;
        }
        await processFiles(files);
    };



    useEffect(() => {
        // Set the validation function
        validate.current = handleValidation;
    }, [validate, selectedFiles]);

    return (
        <div className={classes.mainContainer}>
            <div className={classes.textContainer}>
                <h2 className={`${classes.title} titleSemibold`}>Fotos</h2>
                <span className="slimRegularLarge">Nur JPG- und PNG-Dateien. Drag & Drop oder Hochladen.</span>
            </div>

            {selectedFiles.length > 0 && (
                <div
                    className={classes.addMoreContainer}
                    onClick={() => {
                        const fileInput = document.getElementById('file-upload');
                        if (fileInput) {
                            fileInput.click();
                        }
                    }}
                >
                    <div className={classes.addMoreInner}>
                        <span className="slimRegularLarge">Mehr Fotos hinzufügen</span>
                        <span className={classes.addMoreIcon}>
                            <svg xmlns="http://www.w3.org/2000/svg" width="17" height="17" viewBox="0 0 17 17" fill="none">
                                <path fillRule="evenodd" clipRule="evenodd" d="M16.1016 8.63168H0.101562V7.55536H16.1016V8.63168Z" fill="white"/>
                                <path fillRule="evenodd" clipRule="evenodd" d="M8.63589 0.0999756V16.1H7.55957V0.0999756H8.63589Z" fill="white"/>
                                <path fillRule="evenodd" clipRule="evenodd" d="M16.1016 8.63168H0.101562V7.55536H16.1016V8.63168Z" stroke="white" strokeWidth="0.2" strokeMiterlimit="10"/>
                                <path fillRule="evenodd" clipRule="evenodd" d="M8.63589 0.0999756V16.1H7.55957V0.0999756H8.63589Z" stroke="white" strokeWidth="0.2" strokeMiterlimit="10"/>
                            </svg>
                        </span>
                    </div>
                </div>
            )}
            <div 
                style={{ height: selectedFiles.length > 0 ? 'fit-content' : '310px' }}
                className={`${classes.photoPickerContainer} ${selectedFiles.length > 0 ? classes.photoPickerContainerHasImages : ''}`} 
                onDragOver={handleContainerDragOver}
                onDragLeave={handleContainerDragLeave}
                onDrop={handleContainerDrop}
            >
               <div className={`${classes.photoPickerInputContainer} ${isDragging ? classes.dragging : ''}`}>
               {selectedFiles.length > 0 && (
                        <div className={classes.previewContainer}>
                            {selectedFiles.map((photo, index) => (
                                <div 
                                    key={photo.id || `photo-${index}`} 
                                    data-image-index={index}
                                    className={`${classes.previewImageWrapper} ${draggedIndex === index ? classes.dragging : ''} ${dragOverIndex === index ? classes.dragOver : ''}`}
                                    draggable
                                    onDragStart={(e) => handleDragStart(e, index)}
                                    onDragOver={(e) => handleImageDragOver(e, index)}
                                    onDragLeave={handleImageDragLeave}
                                    onDrop={(e) => handleImageDrop(e, index)}
                                    onDragEnd={handleDragEnd}
                                    onTouchStart={(e) => handleTouchStart(e, index)}
                                    onTouchMove={handleTouchMove}
                                    onTouchEnd={handleTouchEnd}
                                >
                                    {index === 0 && (
                                        <span className={`${classes.coverBadge} regularXxs`}>Cover Photo</span>
                                    )}
                                    <PreviewImageWithSkeleton photo={photo} index={index} />
                                    <button
                                        onClick={() => handleRemovePhoto(photo)}
                                        className={classes.removeButton}
                                        type="button"
                                        title={photo.isExisting ? 'Remove existing image' : 'Remove new image'}
                                    >
                                        ×
                                    </button>
                                </div>
                        ))}
                    </div>
                )}

               <input 
                    type="file" 
                    multiple 
                    accept=".jpg,.jpeg,.png,image/jpeg,image/png" 
                    onChange={handleFileChange} 
                    className={`${classes.fileInput} slimRegularLarge`} 
                    style={{ display: 'none' }} 
                    id="file-upload" 
                />
                {selectedFiles.length === 0 && (
                    <label 
                        htmlFor="file-upload" 
                        className={`${classes.uploadButton} mediumLarge`}>
                        Fotos hochladen
                    </label>
                )}
               </div>
            </div>
            {selectedFiles.length > 0 && (
                <div
                    className={classes.addMoreContainer}
                    onClick={() => {
                        const fileInput = document.getElementById('file-upload');
                        if (fileInput) {
                            fileInput.click();
                        }
                    }}
                >
                    <div className={classes.addMoreInner}>
                        <span className="mediumLarge">Mehr Fotos hinzufügen</span>
                        <span className={classes.addMoreIcon}>
                            <svg xmlns="http://www.w3.org/2000/svg" width="17" height="17" viewBox="0 0 17 17" fill="none">
                                <path fillRule="evenodd" clipRule="evenodd" d="M16.1016 8.63168H0.101562V7.55536H16.1016V8.63168Z" fill="white"/>
                                <path fillRule="evenodd" clipRule="evenodd" d="M8.63589 0.0999756V16.1H7.55957V0.0999756H8.63589Z" fill="white"/>
                                <path fillRule="evenodd" clipRule="evenodd" d="M16.1016 8.63168H0.101562V7.55536H16.1016V8.63168Z" stroke="white" strokeWidth="0.2" strokeMiterlimit="10"/>
                                <path fillRule="evenodd" clipRule="evenodd" d="M8.63589 0.0999756V16.1H7.55957V0.0999756H8.63589Z" stroke="white" strokeWidth="0.2" strokeMiterlimit="10"/>
                            </svg>
                        </span>
                    </div>
                </div>
            )}
            {errors.title && <ErrorMessage>{errors.title}</ErrorMessage>}
        </div>
    );
};

export default FotosEditor;